import type { CSSProperties } from "react";

interface WordmarkProps {
  /** Pixel height of the rendered wordmark. Defaults to 24. */
  height?: number;
  /** "ink" (dark) for light backgrounds, "paper" (light) for dark backgrounds. */
  variant?: "ink" | "paper";
  /** Render only the Iris mark (no "klubora" text). */
  markOnly?: boolean;
  /** Optional inline style override (e.g., extra margins). */
  style?: CSSProperties;
}

/**
 * Klubora wordmark — 3 triangular blades (rotated 0/120/240, opacity 1/0.85/0.7)
 * around an indigo center circle, paired with the lowercase "klubora" wordmark.
 * Mirrors apps/site/components/brand/Wordmark.tsx without external CSS deps.
 */
export function Wordmark({
  height = 24,
  variant = "ink",
  markOnly = false,
  style,
}: WordmarkProps) {
  const fontSize = height * 0.75;
  const gap = Math.round(height * 0.33);
  const color = variant === "ink" ? "#0E1116" : "#FAFAF7";

  return (
    <span
      role="img"
      aria-label="Klubora"
      style={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        userSelect: "none",
        height,
        gap: markOnly ? 0 : gap,
        color,
        ...style,
      }}
    >
      <svg
        viewBox="0 0 80 80"
        width={height}
        height={height}
        aria-hidden="true"
        focusable="false"
      >
        <path d="M 40,40 L 71,22 L 40,4 Z" fill="currentColor" />
        <path
          d="M 40,40 L 71,22 L 40,4 Z"
          fill="currentColor"
          opacity={0.85}
          transform="rotate(120 40 40)"
        />
        <path
          d="M 40,40 L 71,22 L 40,4 Z"
          fill="currentColor"
          opacity={0.7}
          transform="rotate(240 40 40)"
        />
        <circle cx="40" cy="40" r="6" fill="#3D4EE8" />
      </svg>
      {!markOnly && (
        <span
          aria-hidden="true"
          style={{
            fontFamily:
              "Inter, 'Söhne', system-ui, -apple-system, 'Segoe UI', sans-serif",
            fontWeight: 500,
            fontSize,
            letterSpacing: "-0.012em",
            lineHeight: 1,
            color: "currentColor",
          }}
        >
          klubora
        </span>
      )}
    </span>
  );
}
